import React, { useCallback, useEffect, useState, useMemo, useRef } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faExclamationTriangle,
  faInfoCircle,
  faCheckCircle,
  faFileInvoice,
  faUser,
  faUniversity,
  faCalculator,
  faEye,
  faLightbulb,
  faArrowLeft,
  faEdit,
  faPrint,
  faSync
} from "@fortawesome/free-solid-svg-icons";

import {
  Card,
  Button,
  Form,
  Modal,
  Container,
  Row,
  Spinner,
  Alert,
  Col,
  Table,
  Badge
} from "@themesberg/react-bootstrap";
import Attachment from "../../../components/Attachment";
import { toast } from "react-hot-toast";
import { useDropzone } from "react-dropzone";
import { useNavigate, useLocation } from "react-router-dom";
import moment from "moment-timezone";
import useQuery from "../../../hooks/useQuery";
import { apiURL, fetchApi, postApi } from "../../../redux/api";
import { Routes } from "../../../routes";
import BackButton from "../../../components/BackButton";
import { 
  resumableUpload, 
  hasPendingUpload, 
  getPendingUploadProgress,
  clearAllPendingUploads 
} from "../../../utils/resumableUpload";
import CustomForm from "../../../components/CustomForm";
import { formatNumber } from "../../../redux/utils";
import CustomButton from "../../../components/CustomButton";
import NewContractorForm from "./NewContractorForm";
import TaxItem from "../contractor-schedule/TaxItem";
import CapitalProjectTaxSection from "./CapitalProjectTaxSection";
import { useSelector } from "react-redux";
import NewPVBankDetails from "./NewPVBankDetails";
import Trailbalance from "./HtmlPvPdf";
import { useResponsive } from "../../../hooks/useResponsive";
import ResponsiveContainer from "../../../components/ResponsiveContainer";
import ResponsiveButton from "../../../components/ResponsiveButton";
import "./preview-styles.css";

export const imageExtensions = [".jpg", ".jpeg", ".png", ".gif"];

export const paymentTypes = ["Direct Payment", "Power of Attorney", "Others"];
export const projectTypes = [
  "Special Project",
  "Constituency Project",
  "Other",
];
export const projectClassifications = [
  "Certificate Number",
  "APG Number",
  "Mobilization",
  "Retention",
  "Land and Building",
  "Plant and Machinery",
  "Furniture and Fittings",
  "Others",
];

export default () => {
  const { isMobile } = useResponsive();
  const [showPDF, setShowPDF] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [modal, setModal] = useState(false);
  const history = useNavigate();
  const location = useLocation();
  const [batchCode, setBatchCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [dataLoading, setDataLoading] = useState(false);
  const [taxesLoading, setTaxesLoading] = useState(false);
  const [taxesList, setTaxesList] = useState([]);
  const [taxesApplied, setTaxesApplied] = useState([]);
  const [errors, setErrors] = useState({});
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const [showHelp, setShowHelp] = useState(false);
  const user = useSelector((state) => state.auth.user);
  const [mda_data, setMda_data] = useState([]);
  const [showTaxModal, setShowTaxModal] = useState(false);
  const [activeTaxItemIndex, setActiveTaxItemIndex] = useState(null);
  const [itemTaxes, setItemTaxes] = useState([]); // Temporary state for modal selection
  const totalAmount = mda_data.reduce((sum, item) => sum + parseFloat(item.amount || 0), 0)

  // Duplicate submission prevention
  const submissionRef = useRef(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const submissionTimeoutRef = useRef(null);

  // Edit mode detection
  const isEditMode = location.pathname.includes('edit') || location.pathname.includes('generated');
  const [pvLoadComplete, setPvLoadComplete] = useState(false);

  const toggle = () => {
    setModal(!modal);
    if (modal === true) {
      history("/dashboard/pv_collection");
    }
  };

  const [pvImageInfo, setPvImageInfo] = useState([]);
  const [attachmentModalIsOpen, setAttachmentModalIsOpen] = useState(false);
  const [attachmentList, setAttachmentList] = useState([]);
  const query = useQuery();
  const imageId1 = query.get("imageId");
  const _id = query.get("id");
  const imageId = moment().format("YYYYMMDDhhmmss");
  const approval_no = query.get("approval_no");
  const pv_code = query.get("pv_code");

  // If pv_code is empty but we have approval_no, we need to fetch by approval_no
  const needsFetchByApprovalNo = isEditMode && !pv_code && approval_no;

  const [form, setForm] = useState({
    pv_code: "",
    date: moment().format("YYYY-MM-DD"),
    project_type: "",
    payment_type: "",
    mda_name: user.mda_name,
    amount: "",
    query_type: isEditMode ? "update_pv" : "insert",
    imageId,
    amount: ""
  });
  const approval_type = query.get("approval_type");

  // Simplified validation function with user-friendly messages
  const validateForm = useCallback(() => {
    const newErrors = {};

    // Date validation with helpful message
    if (!form.date) {
      newErrors.date = "Please select the date for this payment voucher";
    }

    // Amount validation with clear explanation
    if (!form.amount || parseFloat(form.amount) <= 0) {
      newErrors.amount = "Please enter a valid payment amount greater than zero";
    }

    // Contractor details validation (not required for Recurrent)
    if (approval_type !== "Recurrent") {
      if (!form.contractor_name?.trim()) {
        newErrors.contractor_name = "Please enter the contractor's full name";
      }
      if (!form.contractor_phone?.trim()) {
        newErrors.contractor_phone = "Please provide the contractor's phone number";
      }
      if (!form.contractor_tin?.trim()) {
        newErrors.contractor_tin = "Please enter the contractor's TIN (Tax Identification Number)";
      }

      // Bank details validation for Capital projects
      if (approval_type === "Capital") {
        if (!form.contractor_acc_name?.trim()) {
          newErrors.contractor_acc_name = "Please enter the account holder's name as it appears on the bank account";
        }
        if (!form.contractor_acc_no?.trim()) {
          newErrors.contractor_acc_no = "Please provide the bank account number";
        }
        if (!form.contractor_bank_name?.trim()) {
          newErrors.contractor_bank_name = "Please select or enter the bank name";
        }
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [form, approval_type]);

  // Real-time validation
  useEffect(() => {
    if (submitAttempted) {
      validateForm();
    }
  }, [form, submitAttempted, validateForm]);

  // Enhanced form fields with better labels and help text
  const recurrentFormFields = useMemo(() => [
    {
      label: "Payment Date",
      value: form.date,
      name: "date",
      type: "date",
      required: true,
      error: errors.date,
      helpText: "Select the date when this payment should be processed"
    },
    {
      label: "Ministry/Department/Agency",
      value: form.mda_name,
      name: "account_description",
      disabled: true,
      helpText: "This is your organization (automatically filled)"
    },
    {
      label: "Administrative Code",
      value: form.mda_code,
      name: "mda_code",
      disabled: true,
      helpText: "Your organization's unique identifier"
    },
  ], [form.date, form.mda_name, form.mda_code, errors.date]);

  // Enhanced form fields for Capital type with better descriptions
  const capitalFormFields = useMemo(() => [
    {
      label: "Payment Date",
      value: form.date,
      name: "date",
      type: "date",
      required: true,
      error: errors.date,
      helpText: "Select the date when this payment should be processed"
    },
    // {
    //   label: "Project Category",
    //   name: "project_type",
    //   value: form.project_type,
    //   col: 4,
    //   disabled: true,
    //   helpText: "Type of project (automatically determined)"
    // },
    // {
    //   label: "Payment Method",
    //   name: "payment_type",
    //   value: form.payment_type,
    //   col: 4,
    //   disabled: true,
    //   helpText: "How the payment will be made"
    // },
    // {
    //   label: "Payment Classification",
    //   value: form.payment_class,
    //   col: 4,
    //   disabled: true,
    //   helpText: "Category of this payment"
    // },
    {
      label: "Ministry/Department/Agency",
      value: form.mda_name,
      name: "account_description",
      disabled: true,
      helpText: "Your organization (automatically filled)"
    },
    {
      label: "Administrative Code",
      value: form.mda_code,
      name: "mda_code",
      disabled: true,
      helpText: "Your organization's unique identifier"
    },
    {
      label: "Economic Code",
      value: form.economic_code,
      name: "economic",
      helpText: "Budget classification for this expense",
      disabled: true,
    },
    {
      label: "Expense Description",
      value: form.description_code,
      name: "economic",
      disabled: true,
      helpText: "What this payment is for"
    },
    {
      label: `Payment Amount (₦${formatNumber(isEditMode ? form.amount : totalAmount)})`,
      value: isEditMode ? form.amount : formatNumber(totalAmount),
      name: "amount",
      disabled: true,
      required: true,
      error: errors.amount,
      helpText: "Total amount to be paid (from approval document)"
    },
    {
      label: "Programme Code",
      value: form.program_code,
      name: "programme",
      disabled: true,
      helpText: "Budget programme this payment belongs to"
    },
    {
      label: "Payment Description/Narration",
      value: form.narration,
      // name: "programme",
      type: 'label',
      col: 6,
      helpText: "Brief description of what this payment covers"
    },
  ], [form, errors]);


  // Load taxes data with better error handling
  const loadTaxes = useCallback(() => {
    setTaxesLoading(true);
    postApi(`post_taxes`, {
      query_type: "select",
    })
      .then((data) => {
        if (data.result) {
          setTaxesList(data.result);
          toast.success(`Loaded ${data.result.length} available tax types`);
        }
      })
      .catch((err) => {
        console.log(err);
        toast.error("Failed to load tax information. Please try again.");
      })
      .finally(() => {
        setTaxesLoading(false);
      });
  }, []);

  useEffect(() => {
    loadTaxes();
  }, [loadTaxes]);

  // Load existing PV data if in edit mode
  useEffect(() => {
    if (isEditMode && pv_code && !pvLoadComplete) {
      console.log('[PV_EDIT] Loading PV data for editing:', pv_code);
      setDataLoading(true);

      postApi(`get_pv_collection`, {
        form: {
          query_type: "select_by_pvcode",
          pv_code,
          mda_code: user?.mda_code,
        },
      })
        .then((data) => {
          if (data?.result?.length) {
            const pvData = data.result[0];
            console.log('[PV_EDIT] PV data loaded:', pvData);

            // Map database fields to form fields with proper field name conversion
            setForm((prev) => ({
              ...prev,
              ...pvData,
              query_type: "update_pv",
              pv_code: pvData.pv_code,
              imageId: pvData.imageId || imageId,
              // Map bank details fields
              mda_account_name: pvData.account_name || pvData.mda_account_name || '',
              mda_account_no: pvData.account_number || pvData.mda_account_no || '',
              mda_account_bank_name: pvData.bank_name || pvData.mda_account_bank_name || '',
              mda_account_type: pvData.account_type || pvData.mda_account_type || '',
              mda_account_sort_code: pvData.sort_code || pvData.mda_account_sort_code || '',
              amount: pvData.amount || '',
              // Ensure contractor fields are populated
              contractor_name: pvData.contractor_name || '',
              contractor_phone: pvData.contractor_phone || '',
              contractor_tin: pvData.contractor_tin || '',
              contractor_email: pvData.contractor_email || '',
              contractor_address: pvData.contractor_address || '',
              contractor_acc_name: pvData.contractor_acc_name || '',
              contractor_acc_no: pvData.contractor_acc_no || '',
              contractor_bank_name: pvData.contractor_bank_name || '',
              contractor_code: pvData.contractor_code || '',
              // Sub-organization fields
              sub: pvData.sub || '',
              sub_sub: pvData.sub_sub || '',
              sub_sub_sub: pvData.sub_sub_sub || '',
              payment_to: pvData.payment_to || '',
            }));

            setPvLoadComplete(true);
            toast.success("PV data loaded successfully for editing");
          } else {
            console.warn('[PV_EDIT] No PV data found');
            toast.error('PV data not found');
          }
        })
        .catch((err) => {
          console.error('[PV_EDIT] Error loading PV:', err);
          toast.error('Failed to load PV data');
        })
        .finally(() => {
          setDataLoading(false);
        });
    }
  }, [isEditMode, pv_code, user?.mda_code, pvLoadComplete, imageId]);

  // Load existing taxes if in edit mode
  useEffect(() => {
    if (isEditMode && pv_code && pvLoadComplete) {
      console.log('[PV_EDIT] Loading applied taxes for:', pv_code);
      fetchApi(`get_taxes?contractor_code=${pv_code}&query_type=tax_schedule&batch_id=${approval_no}`)
        .then((data) => {
          if (data.result && data.result.length) {
            setTaxesApplied(data.result);
            console.log('[PV_EDIT] Taxes loaded:', data.result.length);
          }
        })
        .catch((err) => console.log(err));
    }
  }, [isEditMode, pv_code, pvLoadComplete]);

  // Load existing attachments if in edit mode
  useEffect(() => {
    if (isEditMode && pvLoadComplete && form.imageId) {
      console.log('[PV_EDIT] Loading attachments for:', form.imageId);
      fetchApi(`fetch-pv-images?pv_no=${form.imageId}`)
        .then((resp) => {
          if (resp.results && resp.results.length) {
            const oldAttachments = resp.results.map((itm) => ({
              ...itm,
              old: true,
            }));
            setAttachmentList(oldAttachments);
            console.log('[PV_EDIT] Attachments loaded:', oldAttachments.length);
          }
        })
        .catch((err) => console.log(err));
    }
  }, [isEditMode, pvLoadComplete, form.imageId]);

  // Load approval data with enhanced feedback
  useEffect(() => {
    if (approval_no && !isEditMode) {
      setDataLoading(true);
      postApi(`post_approval_collection`, {
        form: {
          query_type: "select_by_approval_no_group",
          filter: "Capital",
          approval_no,
          id: approval_no,
          mda_code: user?.mda_code
        },
      })
        .then((data) => {
          if (data && data.result && data.result.length) {
            setMda_data(data.result);
            setForm((p) => ({
              ...p,
              ...data.result[0],
              mda_name: data.result[0].mda_name && data.result[0].mda_name !== ""
                ? data.result[0].mda_name
                : user.mda_name
            }));
            // toast.success("Approval information loaded successfully");
          } else {
            toast.warning("No approval data found for this approval number");
          }
        })
        .catch((err) => {
          console.log(err);
          toast.error("Failed to load approval information. Please check the approval number.");
        })
        .finally(() => {
          setDataLoading(false);
        });
    }
  }, [approval_no, user?.mda_code, user.mda_name, isEditMode]);

  // Load approval data for edit mode (to get line items)
  useEffect(() => {
    if (isEditMode && approval_no && pvLoadComplete) {
      console.log('[PV_EDIT] Loading approval data for line items');
      setDataLoading(true);
      postApi(`post_approval_collection`, {
        form: {
          query_type: "select_by_approval_no_group",
          filter: approval_type,
          approval_no,
          id: approval_no,
          mda_code: user?.mda_code
        },
      })
        .then((data) => {
          if (data && data.result && data.result.length) {
            setMda_data(data.result);
            console.log('[PV_EDIT] Approval line items loaded:', data.result.length);
          }
        })
        .catch((err) => {
          console.log(err);
          toast.error("Failed to load approval line items");
        })
        .finally(() => {
          setDataLoading(false);
        });
    }
  }, [isEditMode, approval_no, approval_type, user?.mda_code, pvLoadComplete]);

  // Simplified form change handler
  const handleChange = useCallback(({ target: { name, value } }) => {
    setForm((prev) => ({ ...prev, [name]: value }));

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: null }));
    }
  }, [errors]);

  // File upload and attachment handling
  const {
    acceptedFiles,
    getRootProps,
    getInputProps,
    isDragActive,
    isDragAccept,
    isDragReject,
  } = useDropzone({ accept: "image/*, .pdf, text/plain" });

  const attachmentModalToggle = () => {
    setAttachmentModalIsOpen((p) => !p);
  };

  const viewAttachment = () => {
    fetchApi(`fetch-pv-images?pv_no=${imageId1}`, (resp) => {
      attachmentModalToggle();
      if (resp.results && resp.results.length) {
        setPvImageInfo(resp.results);
      } else {
        setPvImageInfo({});
      }
    });
  };

  const handleApi1 = (pv_no) => {
    // console.log(form)

    // if (form.approved_amount && form.approved_amount !== '') {
    const formData = new FormData();
    form.pv_no = pv_no;
    for (let i = 0; i < acceptedFiles.length; i++) {
      formData.append("files", acceptedFiles[i]);
    }

    // Upload function with XMLHttpRequest for real progress tracking and retry logic
    const uploadWithProgressAndRetry = (formData, maxRetries = 3, timeoutMs = 300000) => {
      return new Promise((resolve, reject) => {
        let retryCount = 0;

        const attemptUpload = () => {
          const xhr = new XMLHttpRequest();
          let timeoutId;

          // Progress tracking
          xhr.upload.addEventListener('progress', (e) => {
            if (e.lengthComputable) {
              const percentComplete = Math.round((e.loaded / e.total) * 100);
              console.log(`Upload progress: ${percentComplete}%`);
              
              // Update toast with progress
              toast.loading(`Uploading attachments... ${percentComplete}%`, { 
                id: "upload-progress" 
              });
            }
          });

          // Upload complete
          xhr.addEventListener('load', () => {
            clearTimeout(timeoutId);
            
            if (xhr.status >= 200 && xhr.status < 300) {
              try {
                const result = JSON.parse(xhr.responseText);
                toast.dismiss("upload-progress");
                resolve(result);
              } catch (err) {
                toast.dismiss("upload-progress");
                reject(new Error('Invalid server response'));
              }
            } else {
              toast.dismiss("upload-progress");
              
              // Retry on server errors (5xx) or network errors
              if ((xhr.status >= 500 || xhr.status === 0) && retryCount < maxRetries) {
                retryCount++;
                console.log(`Upload failed, retrying... (${retryCount}/${maxRetries})`);
                toast.loading(`Upload failed, retrying... (${retryCount}/${maxRetries})`, { 
                  id: "upload-retry" 
                });
                
                // Exponential backoff: 1s, 2s, 4s
                setTimeout(() => {
                  toast.dismiss("upload-retry");
                  attemptUpload();
                }, Math.pow(2, retryCount - 1) * 1000);
              } else {
                try {
                  const errorResult = JSON.parse(xhr.responseText);
                  reject(new Error(errorResult.message || `Upload failed with status ${xhr.status}`));
                } catch {
                  reject(new Error(`Upload failed with status ${xhr.status}`));
                }
              }
            }
          });

          // Upload error
          xhr.addEventListener('error', () => {
            clearTimeout(timeoutId);
            toast.dismiss("upload-progress");
            
            // Retry on network errors
            if (retryCount < maxRetries) {
              retryCount++;
              console.log(`Network error, retrying... (${retryCount}/${maxRetries})`);
              toast.loading(`Network error, retrying... (${retryCount}/${maxRetries})`, { 
                id: "upload-retry" 
              });
              
              setTimeout(() => {
                toast.dismiss("upload-retry");
                attemptUpload();
              }, Math.pow(2, retryCount - 1) * 1000);
            } else {
              reject(new Error('Network error - please check your connection'));
            }
          });

          // Upload abort/timeout
          xhr.addEventListener('abort', () => {
            clearTimeout(timeoutId);
            toast.dismiss("upload-progress");
            reject(new Error('Upload cancelled'));
          });

          // Set timeout
          timeoutId = setTimeout(() => {
            xhr.abort();
            toast.dismiss("upload-progress");
            
            // Retry on timeout
            if (retryCount < maxRetries) {
              retryCount++;
              console.log(`Upload timeout, retrying... (${retryCount}/${maxRetries})`);
              toast.loading(`Upload timeout, retrying... (${retryCount}/${maxRetries})`, { 
                id: "upload-retry" 
              });
              
              setTimeout(() => {
                toast.dismiss("upload-retry");
                attemptUpload();
              }, Math.pow(2, retryCount - 1) * 1000);
            } else {
              reject(new Error('Upload timeout - files may be too large or network too slow'));
            }
          }, timeoutMs);

          // Start upload
          xhr.open('POST', `${apiURL}/post_images_pv`, true);
          xhr.send(formData);
        };

        // Start first attempt
        attemptUpload();
      });
    };

    if (!acceptedFiles.length) {
      setForm((p) => ({ ...p, approval: "not approved" }));
      formData.append("form", JSON.stringify(form));

      toast.loading("Saving PV...", { id: "upload-progress" });

      uploadWithProgressAndRetry(formData)
        .then((result) => {
          console.log(result);
          toast.dismiss("upload-progress");
          if (result.status === "success") {
            toast.success(result.message || "PV saved successfully (no attachments)");
          } else {
            toast.error(result.message || "Failed to save PV");
          }
        })
        .catch((err) => {
          console.error("Upload error:", err);
          toast.dismiss("upload-progress");
          toast.error(err.message || "Upload failed");
        });
    } else {
      formData.append("form", JSON.stringify(form));

      toast.loading("Preparing upload...", { id: "upload-progress" });

      uploadWithProgressAndRetry(formData)
        .then((result) => {
          console.log(result);
          toast.dismiss("upload-progress");
          if (result.status === "success") {
            toast.success(
              result.message || 
              `${result.filesUploaded || acceptedFiles.length} file(s) uploaded successfully`,
              { duration: 4000 }
            );
          } else {
            toast.error(result.message || "Failed to upload attachments");
          }
        })
        .catch((err) => {
          console.error("Upload error:", err);
          toast.dismiss("upload-progress");
          toast.error(err.message || "Upload failed", { duration: 5000 });
        });
    }
    // Object.keys(form).forEach(item => {
    //   formData.append(item, form[item])
    // })

    // formData.append('user', name.email)
    //  formData.append('group_id', groupName)
    // } else {
    //   alert('Please enter a valid amount')
    // }
  };

  // Enhanced API submission with better user feedback and error handling
  const handleApi = useCallback((e) => {
    // Prevent default form submission
    if (e && e.preventDefault) {
      e.preventDefault();
    }

    // CRITICAL: Prevent duplicate submissions
    if (isSubmitting || submissionRef.current) {
      console.warn('[PV_CREATION] Duplicate submission attempt blocked');
      toast.warning('Submission already in progress. Please wait...', { duration: 3000 });
      return;
    }

    setSubmitAttempted(true);

    if (!validateForm()) {
      // Count the number of errors and provide helpful feedback
      const errorFields = Object.keys(errors);
      const errorCount = errorFields.length;

      toast.error(
        `Please complete ${errorCount} required field${errorCount > 1 ? 's' : ''} before submitting`,
        { duration: 4000 }
      );

      // Scroll to first error field with smooth animation
      const firstErrorField = document.querySelector('.is-invalid');
      if (firstErrorField) {
        firstErrorField.scrollIntoView({ behavior: 'smooth', block: 'center' });
        setTimeout(() => firstErrorField.focus(), 500);
      }
      return;
    }

    // Set submission flags
    submissionRef.current = true;
    setIsSubmitting(true);
    setLoading(true);

    // Log submission attempt
    console.log(`[PV_${isEditMode ? 'UPDATE' : 'CREATION'}] Starting submission`, {
      approval_no,
      approval_type,
      pv_code: isEditMode ? pv_code : 'new',
      user: user.username,
      timestamp: new Date().toISOString(),
      list_count: mda_data.length,
      taxes_count: taxesApplied.length,
      mode: isEditMode ? 'EDIT' : 'CREATE'
    });

    // Show progress feedback to user
    const isCapitalProject = approval_type === "Capital" || approval_type === "Recurrent";
    const progressMessage = isEditMode
      ? "Updating payment voucher..."
      : isCapitalProject
        ? "Creating capital project payment vouchers..."
        : "Creating payment voucher...";

    toast.loading(progressMessage, { id: 'pv-operation' });

    // Helper function to update budget code
    const updateBudgetCode = (description, query_type) => {
      return postApi(`update-budget-code`, { description, query_type })
        .then((d) => {
          if (d.result) {
            setBatchCode(d.pv_code);
          }
        })
        .catch((err) => console.log(err));
    };

    if (isCapitalProject && !isEditMode) {
      if (approval_type === "Recurrent") {
        // Sequentially create individual PVs for each line item in recurrent payments
        // This ensures each item gets its own PV code and correctly attached taxes as requested
        (async () => {
          let successCount = 0;
          let totalGrossAmount = 0;
          let totalTaxAmount = 0;
          let lastPvCode = "";

          for (let i = 0; i < mda_data.length; i++) {
            const item = mda_data[i];
            const itemApprovalNo = `${approval_no}`; // Suffixed to ensure unique PV code generation

            // Map tax_name to description for backend compatibility (CALL tax_entry expects :description)
            const itemTaxes = (item.applied_taxes || []).map(tax => ({
              ...tax,
              description: tax.tax_name
            }));

            try {
              const data = await postApi(`create_pv`, {
                approval_type,
                approval_no: itemApprovalNo,
                form: {
                  ...form,
                  amount: item.amount,
                  description_code: item.description_code,
                  economic_code: item.economic_code,
                  approval_no: itemApprovalNo,
                  approval_type,
                  userId: user.username,
                },
                list: [{
                  approval_no: itemApprovalNo,
                  userId: user.username,
                  ...form,
                  ...item,
                  account_name: form.mda_account_name,
                  bank_name: form.mda_account_bank_name,
                  account_type: form.mda_account_type,
                  sort_code: form.mda_account_sort_code,
                  account_number: form.mda_acc_no || form.mda_account_no,
                  sub: form.sub,
                  sub_sub: form.sub_sub,
                  sub_sub_sub: form.sub_sub_sub,
                  payment_to: form.payment_to,
                }],
                taxesApplied: itemTaxes,
                userId: user.username,
                notif_key: "Check PV",
              });

              if (data.success) {
                successCount++;
                totalGrossAmount += parseFloat(item.amount || 0);
                totalTaxAmount += itemTaxes.reduce((sum, t) => sum + parseFloat(t.amount || 0), 0);
                lastPvCode = data.pv_code;
                handleApi1(data.pv_code);
              }
            } catch (err) {
              console.error(`[PV_CREATION] Error creating Recurrent PV for item ${i + 1}:`, err);
            }
          }

          if (successCount > 0) {
            updateBudgetCode("pv_code", "update");

            toast.success(
              `✅ ${successCount} Recurrent PVs Created Successfully!\n` +
              `Last PV Code: ${lastPvCode}\n` +
              `Gross Total: ₦${totalGrossAmount.toLocaleString()}\n` +
              `Total Taxes: ₦${totalTaxAmount.toLocaleString()}\n` +
              `Net Total: ₦${(totalGrossAmount - totalTaxAmount).toLocaleString()}`,
              { id: 'pv-operation', duration: 8000 }
            );

            setBatchCode(lastPvCode);
            toggle();
            history(
              `${Routes.cashBacking.NewViewPVPDf}?approval_no=${encodeURIComponent(approval_no)}&approval_type=${encodeURIComponent(approval_type)}&pv_code=${encodeURIComponent(lastPvCode)}`
            );
          } else {
            toast.error("Failed to create Recurrent PVs. Please check your inputs and try again.", { id: 'pv-operation' });
          }

          setLoading(false);
          if (submissionTimeoutRef.current) clearTimeout(submissionTimeoutRef.current);
          submissionTimeoutRef.current = setTimeout(() => {
            submissionRef.current = false;
            setIsSubmitting(false);
          }, 5000);
        })();
      } else {
        // Standard Capital Project logic remains batch-based
        postApi(`create_pv`, {
          approval_type,
          approval_no,
          form: {
            ...form,
            approval_no,
            approval_type,
            userId: user.username,
          },
          list: mda_data.map((d) => ({
            approval_no,
            userId: user.username,
            ...form,
            ...d,
            account_name: form.mda_account_name,
            bank_name: form.mda_account_bank_name,
            account_type: form.mda_account_type,
            sort_code: form.mda_account_sort_code,
            account_number: form.mda_acc_no || form.mda_account_no,
            sub: form.sub,
            sub_sub: form.sub_sub,
            sub_sub_sub: form.sub_sub_sub,
            payment_to: form.payment_to,
          })),
          taxesApplied,
          notif_key: "Check PV",
        })
          .then((data) => {
            if (data.success) {
              handleApi1(data.pv_code);
              updateBudgetCode("pv_code", "update");

              const totalTaxAmount = taxesApplied.reduce((sum, tax) => sum + parseFloat(tax.amount || 0), 0);
              const netAmount = parseFloat(totalAmount || 0) - totalTaxAmount;

              toast.success(
                `✅ Capital Project PVs Created Successfully!\n` +
                `Main PV Code: ${data.pv_code}\n` +
                `Total PVs Created: ${data.total_pvs || (1 + taxesApplied.length)}\n` +
                `Gross Amount: ₦${parseFloat(totalAmount || 0).toLocaleString()}\n` +
                `Tax Deductions: ₦${totalTaxAmount.toLocaleString()}\n` +
                `Net Amount: ₦${netAmount.toLocaleString()}`,
                { id: 'pv-operation', duration: 8000 }
              );

              setBatchCode(data.pv_code);
              toggle();

              history(
                `${Routes.cashBacking.NewViewPVPDf}?approval_no=${encodeURIComponent(approval_no)}&approval_type=${encodeURIComponent(approval_type)}&pv_code=${encodeURIComponent(data.pv_code)}`
              );
            } else if (data.error === 'DUPLICATE_PV') {
              console.warn('[PV_CREATION] Duplicate PV detected:', data);
              toast.error(
                `⚠️ Payment Voucher Already Exists!\n` +
                `PV Code: ${data.existing_pv_code}\n` +
                `Please check existing PVs.`,
                { id: 'pv-operation', duration: 10000 }
              );
              if (data.existing_pv_code) {
                setTimeout(() => {
                  history(
                    `${Routes.cashBacking.NewViewPVPDf}?approval_no=${encodeURIComponent(approval_no)}&approval_type=${encodeURIComponent(approval_type)}&pv_code=${encodeURIComponent(data.existing_pv_code)}`
                  );
                }, 2000);
              }
            } else {
              toast.error(data.message || data.error || "Failed to create capital project PVs.", { id: 'pv-operation' });
            }
          })
          .catch((error) => {
            console.error('[PV_CREATION] Error:', error);
            toast.error("Something went wrong while creating the PV.", { id: 'pv-operation' });
            submissionRef.current = false;
            setIsSubmitting(false);
          })
          .finally(() => {
            setLoading(false);
            if (submissionTimeoutRef.current) clearTimeout(submissionTimeoutRef.current);
            submissionTimeoutRef.current = setTimeout(() => {
              submissionRef.current = false;
              setIsSubmitting(false);
            }, 5000);
          });
      }
    } else {
      // Standard PV creation or update
      postApi(`post_pv_collection`, {
        form: {
          ...form,
          query_type: isEditMode ? "update_pv" : "insert",
          pv_code: isEditMode ? pv_code : undefined,
          approval_no,
          userId: user.username,
        },
        list: mda_data.map((d) => ({
          approval_no,
          userId: user.username,
          ...form,
          ...d,
          account_name: form.mda_account_name,
          bank_name: form.mda_account_bank_name,
          account_type: form.mda_account_type,
          sort_code: form.mda_account_sort_code,
          account_number: form.mda_acc_no || form.mda_account_no,
          sub: form.sub,
          sub_sub: form.sub_sub,
          sub_sub_sub: form.sub_sub_sub,
          payment_to: form.payment_to,
        })),
        taxesApplied,
        filesData: isEditMode ? attachmentList : undefined,
        notif_key: "Check PV",
      })
        .then((data) => {
          if (data.success) {
            handleApi1(data.approval_no || data.pv_code);
            if (!isEditMode) {
              updateBudgetCode("pv_code", "update");
            }

            const successMessage = isEditMode
              ? `✅ Payment Voucher Updated Successfully!\nPV Code: ${pv_code}`
              : `✅ Payment Voucher Created Successfully!\nPV Code: ${data.pv_code}\nAmount: ₦${parseFloat(totalAmount || 0).toLocaleString()}`;

            toast.success(successMessage, { id: 'pv-operation', duration: 5000 });

            setBatchCode(data.pv_code || pv_code);
            toggle();

            // Navigate to view the PV
            const finalPvCode = isEditMode ? pv_code : data.pv_code;

            // Special handling for OAG
            if (user.mda_name === "Office of the Accountant-General" && !isEditMode) {
              postApi(`update-pv`, {
                pv_code: finalPvCode,
                approval_no: approval_no,
                status: "Receiving Office Approved",
                userId: user.username,
                remarks: "",
              });
              history(
                `${Routes.pv.pvReportView}/${approval_no}?approval_type=${approval_type}`
              );
            } else {
              history(
                `${Routes.cashBacking.NewViewPVPDf}?approval_no=${encodeURIComponent(approval_no)}&approval_type=${encodeURIComponent(approval_type)}&pv_code=${encodeURIComponent(finalPvCode)}`
              );
            }
          } else if (data.error === 'DUPLICATE_PV' && !isEditMode) {
            // Handle duplicate PV error (only for creation, not edit)
            console.warn('[PV_CREATION] Duplicate PV detected:', data);
            toast.error(
              `⚠️ Payment Voucher Already Exists!\n` +
              `PV Code: ${data.existing_pv_code}\n` +
              `This approval already has ${data.existing_count} PV${data.existing_count > 1 ? 's' : ''} created.\n` +
              `Please check existing PVs before creating a new one.`,
              { id: 'pv-operation', duration: 10000 }
            );

            // Navigate to view the existing PV
            if (data.existing_pv_code) {
              setTimeout(() => {
                history(
                  `${Routes.cashBacking.NewViewPVPDf}?approval_no=${encodeURIComponent(approval_no)}&approval_type=${encodeURIComponent(approval_type)}&pv_code=${encodeURIComponent(data.existing_pv_code)}`
                );
              }, 2000);
            }
          } else {
            const errorMessage = isEditMode
              ? "Failed to update payment voucher. Please try again."
              : "Failed to create payment voucher. Please try again.";
            toast.error(data.message || errorMessage, { id: 'pv-operation' });
          }
        })
        .catch((error) => {
          console.error(`[PV_${isEditMode ? 'UPDATE' : 'CREATION'}] Error:`, error);
          toast.error("Something went wrong. Please check your connection and try again.", { id: 'pv-operation' });
          // Reset submission flags on error
          submissionRef.current = false;
          setIsSubmitting(false);
        })
        .finally(() => {
          setLoading(false);
          // Keep submission lock for 5 seconds after completion to prevent rapid resubmission
          if (submissionTimeoutRef.current) {
            clearTimeout(submissionTimeoutRef.current);
          }
          submissionTimeoutRef.current = setTimeout(() => {
            submissionRef.current = false;
            setIsSubmitting(false);
          }, 5000);
        });
    }
  }, [form, approval_no, user.username, mda_data, taxesApplied, approval_type, validateForm, errors, history, isSubmitting, isEditMode, pv_code, totalAmount, attachmentList]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (submissionTimeoutRef.current) {
        clearTimeout(submissionTimeoutRef.current);
      }
    };
  }, []);

  // Preview function - validates form and shows preview without submitting
  const handlePreview = useCallback((e) => {
    if (e && e.preventDefault) {
      e.preventDefault();
    }

    setSubmitAttempted(true);

    if (!validateForm()) {
      const errorFields = Object.keys(errors);
      const errorCount = errorFields.length;

      toast.error(
        `Please complete ${errorCount} required field${errorCount > 1 ? 's' : ''} before previewing`,
        { duration: 4000 }
      );

      const firstErrorField = document.querySelector('.is-invalid');
      if (firstErrorField) {
        firstErrorField.scrollIntoView({ behavior: 'smooth', block: 'center' });
        setTimeout(() => firstErrorField.focus(), 500);
      }
      return;
    }

    // Show preview
    setShowPreview(true);
    toast.success("Preview generated! Review your PV before final submission.", { duration: 3000 });
  }, [validateForm, errors]);

  // Function to go back to edit mode from preview
  const handleBackToEdit = useCallback(() => {
    setShowPreview(false);
  }, []);

  return (
    <ResponsiveContainer>
      <BackButton />

      {/* Simple Header */}
      <Card className={`mb-3 ${isMobile ? 'card-mobile' : ''}`}>
        <Card.Body className="py-3">
          <Row className="align-items-center">
            <Col xs={12} md={8} className={isMobile ? 'mb-2' : ''}>
              <h4 className="mb-1">{isEditMode ? 'Edit' : 'Create'} Payment Voucher</h4>
              <div className="text-muted">
                <small>Approval No: {approval_no} | Type: {approval_type}</small>
                {isEditMode && pv_code && (
                  <small className="ms-2">| PV Code: {pv_code}</small>
                )}
              </div>
            </Col>
            <Col xs={12} md={4} className={isMobile ? 'text-start' : 'text-end'}>
              <ResponsiveButton
                variant="outline-secondary"
                size="sm"
                onClick={() => setShowHelp(!showHelp)}
                className="me-2"
                mobileFullWidth={false}
              >
                <FontAwesomeIcon icon={faInfoCircle} className="me-1" />
                {showHelp ? 'Hide' : 'Show'} Help
              </ResponsiveButton>

              <ResponsiveButton
                variant="outline-primary"
                size="sm"
                onClick={() =>
                  history(
                    `${Routes.cashBacking.releaseCollectionHTML}?approval_no=${encodeURIComponent(approval_no)}`
                  )
                }
              >
                <FontAwesomeIcon icon={faEye} className="me-1" />
                View Approval
              </ResponsiveButton>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Tax Selection Modal for Individual Line Items */}
      <Modal show={showTaxModal} onHide={() => setShowTaxModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Select Taxes for Line Item</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {activeTaxItemIndex !== null && (
            <div className="mb-3">
              <p><strong>Item:</strong> {mda_data[activeTaxItemIndex].description_code}</p>
              <p><strong>Amount:</strong> ₦{formatNumber(mda_data[activeTaxItemIndex].amount)}</p>
              <hr />
              <Row>
                {taxesList.map((tax, idx) => {
                  const isSelected = itemTaxes.some(t => t.tax_name === tax.tax_name);
                  const amount = tax.rate_type === 'percentage'
                    ? ((parseFloat(tax.rate) / 100) * parseFloat(mda_data[activeTaxItemIndex].amount)).toFixed(2)
                    : tax.rate;

                  return (
                    <Col md={4} key={idx} className="mb-2">
                      <Form.Check
                        type="checkbox"
                        id={`tax-${idx}`}
                        label={`${tax.tax_name} (${tax.rate}${tax.rate_type === 'percentage' ? '%' : ''})`}
                        checked={isSelected}
                        onChange={() => {
                          if (isSelected) {
                            setItemTaxes(itemTaxes.filter(t => t.tax_name !== tax.tax_name));
                          } else {
                            setItemTaxes([...itemTaxes, { ...tax, amount }]);
                          }
                        }}
                      />
                    </Col>
                  );
                })}
              </Row>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowTaxModal(false)}>
            Close
          </Button>
          <Button variant="primary" onClick={() => {
            const updatedMdaData = [...mda_data];
            updatedMdaData[activeTaxItemIndex] = {
              ...updatedMdaData[activeTaxItemIndex],
              applied_taxes: itemTaxes
            };
            setMda_data(updatedMdaData);
            setShowTaxModal(false);
            toast.success("Taxes updated for line item");
          }}>
            Save Taxes
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Help Section */}
      {showHelp && (
        <Alert variant="light" className="mb-4 border">
          <h6><FontAwesomeIcon icon={faLightbulb} className="me-2" />What you need to do:</h6>
          <ol className="mb-0">
            {approval_type !== "Recurrent" && <li>Enter contractor information (name, phone, TIN)</li>}
            {approval_type === "Capital" && <li>Provide bank account details for payment</li>}
            {approval_type !== "Recurrent" && <li>Review and apply any applicable taxes</li>}
            <li>Attach supporting documents if required</li>
            <li>Review everything and submit</li>
          </ol>
        </Alert>
      )}

      {/* Main Form Card */}
      <Card>
        <Card.Body>
          {dataLoading && (
            <Alert variant="info" className="d-flex align-items-center mb-4">
              <Spinner animation="border" size="sm" className="me-2" />
              Loading approval information...
            </Alert>
          )}

          {showPDF ? (
            <Trailbalance
              form={form}
              user={user}
              data={mda_data}
              taxesList={taxesList}
              taxesApplied={taxesApplied}
            />
          ) : showPreview ? (
            // PREVIEW MODE
            <div className="preview-fade-in">
              {/* Preview Header */}
              <div className="preview-header">
                <Row className="align-items-center">
                  <Col xs={12} md={8}>
                    <h5 className="mb-1">
                      <FontAwesomeIcon icon={faEye} className="me-2" />
                      Payment Voucher Preview
                    </h5>
                    <p className="mb-0">
                      This is how your PV will appear after submission. Review carefully before final submission.
                    </p>
                  </Col>
                  <Col xs={12} md={4} className={`${isMobile ? 'mt-2 text-start' : 'text-end'}`}>
                    <ResponsiveButton
                      variant="outline-secondary"
                      size="sm"
                      onClick={handleBackToEdit}
                      className="me-2"
                    >
                      <FontAwesomeIcon icon={faArrowLeft} className="me-1" />
                      Back to Edit
                    </ResponsiveButton>
                    {/* <ResponsiveButton
                      variant="outline-primary"
                      size="sm"
                      onClick={() => window.print()}
                    >
                      <FontAwesomeIcon icon={faPrint} className="me-1" />
                      Print Preview
                    </ResponsiveButton> */}
                  </Col>
                </Row>
              </div>

              {/* Preview Content */}
              <div className="preview-container">
                <Trailbalance
                  form={form}
                  user={user}
                  data={mda_data}
                  taxesList={taxesList}
                  taxesApplied={taxesApplied}
                  isPreview={true}
                  isMultiplePVs={taxesApplied.length > 0}
                  showTaxSummary={taxesApplied.length > 0}
                />
              </div>

              {/* Preview Actions */}
              <div className="preview-actions text-center">
                {/* Multiple PVs Information */}
                {taxesApplied.length > 0 && (
                  <div className="mb-4">
                    <Alert variant="info" className="d-inline-block text-start">
                      <h6 className="mb-3">
                        <FontAwesomeIcon icon={faInfoCircle} className="me-2" />
                        Multiple Payment Vouchers Will Be Generated
                      </h6>
                      <div className="mb-3">
                        <strong>📋 Main PV (Net Payment):</strong>
                        <ul className="mb-2 mt-1">
                          <li>Contains complete payment details and tax breakdown</li>
                          <li>Amount: ₦{formatNumber(parseFloat(form?.amount || 0) - taxesApplied.reduce((sum, tax) => sum + parseFloat(tax.amount || 0), 0))} (Net after taxes)</li>
                          <li>Shows detailed narration with tax summary</li>
                        </ul>

                        <strong>💰 Tax Deduction PVs ({taxesApplied.length}):</strong>
                        <ul className="mb-2 mt-1">
                          {taxesApplied.map((tax, index) => (
                            <li key={index}>
                              {tax.description}: ₦{formatNumber(tax.amount)}
                              {tax.rate && ` (${tax.rate}%)`}
                            </li>
                          ))}
                        </ul>

                        <div className="bg-light p-2 rounded mt-2">
                          <strong>📊 Summary:</strong><br />
                          Gross Amount: ₦{formatNumber(form?.amount || 0)}<br />
                          Total Taxes: ₦{formatNumber(taxesApplied.reduce((sum, tax) => sum + parseFloat(tax.amount || 0), 0))}<br />
                          Net Payment: ₦{formatNumber(parseFloat(form?.amount || 0) - taxesApplied.reduce((sum, tax) => sum + parseFloat(tax.amount || 0), 0))}<br />
                          <strong>Total PVs: {1 + taxesApplied.length}</strong>
                        </div>
                      </div>
                    </Alert>
                  </div>
                )}

                <div className="d-flex justify-content-center gap-3 flex-wrap">
                  <ResponsiveButton
                    variant="outline-secondary"
                    onClick={handleBackToEdit}
                    className="px-4"
                  >
                    <FontAwesomeIcon icon={faEdit} className="me-2" />
                    Make Changes
                  </ResponsiveButton>

                  <ResponsiveButton
                    variant="success"
                    onClick={handleApi}
                    disabled={loading}
                    className="px-4"
                  >
                    {loading ? (
                      <>
                        <Spinner animation="border" size="sm" className="me-2" />
                        Creating {taxesApplied.length > 0 ? `${1 + taxesApplied.length} PVs` : 'PV'}...
                      </>
                    ) : (
                      <>
                        <FontAwesomeIcon icon={faCheckCircle} className="me-2" />
                        Confirm & Submit {taxesApplied.length > 0 ? `${1 + taxesApplied.length} PVs` : 'PV'}
                      </>
                    )}
                  </ResponsiveButton>
                </div>

                <Alert variant="warning" className="d-inline-block mt-3">
                  <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />
                  <strong>Final Check:</strong> Once submitted, {taxesApplied.length > 0 ? 'these PVs' : 'this PV'} will be sent for approval and cannot be easily modified.
                  {taxesApplied.length > 0 && (
                    <div className="mt-2">
                      <small>All {1 + taxesApplied.length} PVs will be linked together and processed as a group.</small>
                    </div>
                  )}
                </Alert>
              </div>
            </div>
          ) : (
            <>
              <Form onSubmit={handleApi} noValidate>
                {/* Error Summary */}
                {submitAttempted && Object.keys(errors).length > 0 && (
                  <Alert variant="danger" className="mb-4">
                    <h6><FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />Please complete the following:</h6>
                    <ul className="mb-0">
                      {Object.entries(errors).map(([field, error]) => (
                        <li key={field}>{error}</li>
                      ))}
                    </ul>
                  </Alert>
                )}

                {/* Basic Information - Read Only */}
                <div className="mb-4">
                  <h6 className="text-muted mb-3">Payment Information (Read Only)</h6>
                  <div className="bg-light p-3 rounded">
                    {approval_type === "Recurrent" ? (
                      <CustomForm
                        handleChange={handleChange}
                        fields={recurrentFormFields}
                      />
                    ) : (
                      <CustomForm
                        handleChange={handleChange}
                        fields={capitalFormFields}
                      />
                    )}
                  </div>
                </div>

                {/* Budget Breakdown - Read Only for Recurrent and Capital (in edit mode) */}
                {((approval_type === "Recurrent" || (approval_type === "Capital" && isEditMode)) && mda_data.length > 0) && (
                  <div className="mb-4">
                    <h6 className="text-muted mb-3">
                      {approval_type === "Capital" ? "Project Line Items" : "Budget Breakdown"} (Read Only)
                    </h6>
                    <div className="bg-light p-3 rounded">
                      <Table responsive className="mb-0">
                        <thead>
                          <tr>
                            <th>Description</th>
                            <th>Economic Code</th>
                            <th className="text-end">Amount (₦)</th>
                            {approval_type === "Recurrent" && <th className="text-center">Action</th>}
                          </tr>
                        </thead>
                        <tbody>
                          {mda_data.map((item, index) => (
                            <React.Fragment key={index} style={{ border: "1px solid #ddd" }}>
                              <tr style={item.applied_taxes && item.applied_taxes.length ? {
                                borderLeft: "1px solid #28a745",
                                borderRight: "1px solid #28a745",
                                borderTop: "1px solid #28a745",
                              } : {}}>
                                <td>{item.description_code}</td>
                                <td><small className="text-muted">{item.economic_code}</small></td>
                                <td className="text-end">{formatNumber(item.amount)}</td>
                                {approval_type === "Recurrent" && (
                                  <td className="text-center">
                                    <Button
                                      variant="outline-primary"
                                      size="sm"
                                      onClick={() => {
                                        setActiveTaxItemIndex(index);
                                        setItemTaxes(item.applied_taxes || []);
                                        setShowTaxModal(true);
                                      }}
                                    >
                                      <FontAwesomeIcon icon={faCalculator} className="me-1" />
                                      Add Tax
                                    </Button>
                                  </td>
                                )}
                              </tr>
                              {item.applied_taxes && item.applied_taxes.length > 0 && (
                                <tr className="bg-white" style={{
                                  borderLeft: "1px solid #28a745",
                                  borderRight: "1px solid #28a745",
                                  borderBottom: "1px solid #28a745",
                                }}>
                                  <td colSpan={approval_type === "Recurrent" ? 4 : 3} className="py-2 px-4 border-top-0">
                                    <div className="d-flex align-items-start">
                                      <small className="fw-bold text-danger me-3" style={{ fontSize: '0.75rem', minWidth: '80px' }}>
                                        Deductions:
                                      </small>
                                      <div className="flex-grow-1">
                                        <div className="d-flex flex-wrap gap-x-4 mb-1">
                                          {item.applied_taxes.map((tax, tIdx) => (
                                            <div key={tIdx} className="text-muted me-4" style={{ fontSize: '0.75rem' }}>
                                              <span className="me-1">•</span>
                                              {tax.tax_name}: <span className="fw-bold">₦{formatNumber(tax.amount)}</span>
                                            </div>
                                          ))}
                                        </div>
                                        <div className="d-flex gap-4 border-top pt-1 mt-1">
                                          <div className="text-dark fw-bold" style={{ fontSize: '0.75rem' }}>
                                            Total Deduction: <span className="text-danger">₦{formatNumber(item.applied_taxes.reduce((sum, t) => sum + parseFloat(t.amount || 0), 0))}</span>
                                          </div>
                                          <div className="text-success fw-bold" style={{ fontSize: '0.75rem' }}>
                                            Net Amount: ₦{formatNumber(parseFloat(item.amount || 0) - item.applied_taxes.reduce((sum, t) => sum + parseFloat(t.amount || 0), 0))}
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  </td>
                                </tr>

                              )}
                            </React.Fragment>
                          ))}
                          <tr className="fw-bold">
                            <td colSpan="2">Total Amount</td>
                            <td className="text-end">
                              ₦{formatNumber(totalAmount)}
                            </td>
                          </tr>
                        </tbody>
                      </Table>
                      {isEditMode && (
                        <Alert variant="info" className="mt-3 mb-0">
                          <FontAwesomeIcon icon={faInfoCircle} className="me-2" />
                          <small>
                            These line items are from the original approval and cannot be modified during edit.
                            The payee details, bank information, and tax selections below can be updated.
                          </small>
                        </Alert>
                      )}
                    </div>
                  </div>
                )}

                {/* ACTION SECTIONS START HERE */}

                {/* Document Attachment Section */}
                {!_id && (
                  <div className="mb-4">
                    <h6 className="mb-3">
                      <FontAwesomeIcon icon={faFileInvoice} className="me-2" />
                      Supporting Documents <small className="text-muted">(Optional)</small>
                    </h6>
                    <div className="border rounded p-3">
                      {/* Show existing attachments in edit mode */}
                      {isEditMode && attachmentList.length > 0 && (
                        <div className="mb-3">
                          <p className="text-muted mb-2">
                            <FontAwesomeIcon icon={faInfoCircle} className="me-1" />
                            Existing Attachments:
                          </p>
                          <Table bordered size="sm">
                            <thead>
                              <tr>
                                <th>File Name</th>
                                <th className="text-center" style={{ width: '100px' }}>Action</th>
                              </tr>
                            </thead>
                            <tbody>
                              {attachmentList.filter(item => !item.delete).map((item, index) => (
                                <tr key={index}>
                                  <td>
                                    <a
                                      href={`${apiURL}/uploads/${item.image_url}`}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                    >
                                      {item.image_url}
                                    </a>
                                  </td>
                                  <td className="text-center">
                                    <Button
                                      size="sm"
                                      variant="danger"
                                      onClick={() => {
                                        const updatedList = attachmentList.map((att) =>
                                          att.id === item.id ? { ...att, delete: true } : att
                                        );
                                        setAttachmentList(updatedList);
                                        toast.success('Attachment marked for deletion');
                                      }}
                                    >
                                      Delete
                                    </Button>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </Table>
                        </div>
                      )}

                      <Attachment
                        acceptedFiles={acceptedFiles}
                        getRootProps={getRootProps}
                        getInputProps={getInputProps}
                        isDragActive={isDragActive}
                        isDragAccept={isDragAccept}
                        isDragReject={isDragReject}
                      />

                      {isEditMode && (
                        <p className="text-muted mt-2 mb-0">
                          <small>
                            <FontAwesomeIcon icon={faInfoCircle} className="me-1" />
                            Upload new files to add them to existing attachments
                          </small>
                        </p>
                      )}
                    </div>
                  </div>
                )}

                {/* View Existing Attachment */}
                {_id && (
                  <div className="mb-4 text-center">
                    <ResponsiveButton variant="outline-secondary" onClick={viewAttachment}>
                      <FontAwesomeIcon icon={faEye} className="me-2" />
                      View Existing Attachments
                    </ResponsiveButton>
                  </div>
                )}

                {/* Contractor/Organization Information */}
                {approval_type === "Recurrent" ? (
                  <div className="mb-4">
                    <h6 className="mb-3">
                      <FontAwesomeIcon icon={faUniversity} className="me-2" />
                      Organization Bank Details <span className="text-danger">*</span>
                    </h6>
                    <div className="border rounded p-3">
                      <NewPVBankDetails
                        form={form}
                        setForm={setForm}
                        errors={errors}
                        setErrors={setErrors}
                      />
                    </div>
                  </div>
                ) : (
                  <>
                    {/* Contractor Information */}
                    <div className="mb-4">
                      <h6 className="mb-3">
                        <FontAwesomeIcon icon={faUser} className="me-2" />
                        Contractor Information <span className="text-danger">*</span>
                      </h6>
                      <div className="border rounded p-3">
                        <NewContractorForm
                          form={form}
                          setForm={setForm}
                          errors={errors}
                          setErrors={setErrors}
                        />
                      </div>
                    </div>
                  </>
                )}

                {/* Tax Calculations - Now shown for ALL PV types EXCEPT Recurrent */}
                {approval_type !== "Recurrent" && (
                  <div className="mb-4">
                    <div className="d-flex justify-content-between align-items-center mb-3">
                      <h6 className="mb-0">
                        <FontAwesomeIcon icon={faCalculator} className="me-2" />
                        Tax Deductions
                      </h6>
                      <ResponsiveButton
                        variant="outline-secondary"
                        size="sm"
                        onClick={loadTaxes}
                        disabled={taxesLoading}
                        mobileFullWidth={false}
                      >
                        <FontAwesomeIcon
                          icon={faSync}
                          className={`me-1 ${taxesLoading ? 'fa-spin' : ''}`}
                        />
                        Refresh
                      </ResponsiveButton>
                    </div>
                    <>
                      {taxesLoading ? (
                        <div className="text-center py-3">
                          <Spinner animation="border" size="sm" className="me-2" />
                          Loading available taxes...
                        </div>
                      ) : approval_type === "Capital" ? (
                        <CapitalProjectTaxSection
                          form={form}
                          taxesList={taxesList}
                          taxesApplied={taxesApplied}
                          setTaxesApplied={setTaxesApplied}
                          taxesLoading={taxesLoading}
                        />
                      ) : (
                        <>
                          <p className="text-muted mb-3">
                            <FontAwesomeIcon icon={faInfoCircle} className="me-1" />
                            Select applicable tax deductions. Tax amounts will be automatically calculated and deducted from the payment.
                          </p>
                          {taxesList.length > 0 ? (
                            <Row>
                              {taxesList.map((item, index) => (
                                <TaxItem
                                  key={index}
                                  index={index}
                                  item={item}
                                  form={form}
                                  taxesApplied={taxesApplied}
                                  setTaxesApplied={setTaxesApplied}
                                />
                              ))}
                            </Row>
                          ) : (
                            <Alert variant="info" className="mb-0">
                              <FontAwesomeIcon icon={faInfoCircle} className="me-2" />
                              No tax types available. Click the refresh button to reload.
                            </Alert>
                          )}
                        </>
                      )}
                    </>
                  </div>
                )}

                {/* Payment Summary */}
                {form.amount && (
                  <div className="mb-4">
                    <h6 className="mb-3">Payment Summary</h6>
                    <div className="bg-light p-3 rounded">
                      <Row className="text-center">
                        <Col md={4}>
                          <div className="mb-2">
                            <strong>Gross Amount</strong>
                            <div className="h5 mb-0">₦{formatNumber(totalAmount)}</div>
                          </div>
                        </Col>
                        {taxesApplied.length > 0 && (
                          <Col md={4}>
                            <div className="mb-2">
                              <strong>Tax Deductions</strong>
                              <div className="h5 mb-0 text-warning">
                                -₦{formatNumber(taxesApplied.reduce((sum, tax) => sum + parseFloat(tax.amount || 0), 0))}
                              </div>
                            </div>
                          </Col>
                        )}
                        <Col md={taxesApplied.length > 0 ? 4 : 8}>
                          <div className="mb-2">
                            <strong>Net Payment</strong>
                            <div className="h5 mb-0 text-success">
                              ₦{formatNumber(
                                totalAmount -
                                taxesApplied.reduce((sum, tax) => sum + parseFloat(tax.amount || 0), 0)
                              )}
                            </div>
                          </div>
                        </Col>
                      </Row>
                    </div>
                  </div>
                )}

                {/* Submit Buttons */}
                <div className="text-center">
                  <div className="d-flex justify-content-center gap-3 flex-wrap">
                    {/* Preview Button */}
                    <ResponsiveButton
                      variant="outline-primary"
                      onClick={handlePreview}
                      disabled={loading || dataLoading || isSubmitting}
                      className="px-4"
                    >
                      <FontAwesomeIcon icon={faEye} className="me-2" />
                      Preview PV
                    </ResponsiveButton>

                    {/* Direct Submit Button */}
                    <ResponsiveButton
                      type="submit"
                      variant="success"
                      disabled={loading || dataLoading || isSubmitting}
                      className="px-4"
                    >
                      {loading || isSubmitting ? (
                        <>
                          <Spinner animation="border" size="sm" className="me-2" />
                          {isEditMode ? 'Updating PV...' : 'Creating PV...'}
                        </>
                      ) : (
                        <>
                          <FontAwesomeIcon icon={faCheckCircle} className="me-2" />
                          {isEditMode ? 'Update & Submit PV' : 'Create & Submit PV'}
                        </>
                      )}
                    </ResponsiveButton>
                  </div>

                  {/* Submission Warning */}
                  {isSubmitting && (
                    <Alert variant="warning" className="mt-3 text-center">
                      <Spinner animation="border" size="sm" className="me-2" />
                      <strong>Creating Payment Voucher...</strong>
                      <br />
                      <small>Please do not refresh, navigate away, or click submit again.</small>
                      <br />
                      <small>This may take a few moments.</small>
                    </Alert>
                  )}

                  <div className="mt-3">
                    <small className="text-muted d-block">
                      <FontAwesomeIcon icon={faLightbulb} className="me-1 text-warning" />
                      <strong>Tip:</strong> Use "Preview PV" to see exactly how your payment voucher will look before {isEditMode ? 'updating' : 'submitting'}.
                    </small>
                    <small className="text-muted">
                      Or click "{isEditMode ? 'Update' : 'Create'} & Submit PV" to {isEditMode ? 'update' : 'create'} the voucher directly.
                    </small>
                  </div>
                </div>
              </Form>

              <ViewImage
                attachmentModalIsOpen={attachmentModalIsOpen}
                attachmentModalToggle={attachmentModalToggle}
                pvImageInfo={pvImageInfo}
                imageExtensions={imageExtensions}
              />
            </>
          )}
        </Card.Body>
      </Card>
    </ResponsiveContainer>
  );
};

export const ViewImage = ({
  attachmentModalIsOpen = false,
  attachmentModalToggle = (f) => f,
  pvImageInfo = [],
  imageExtensions = [],
}) => {
  return (
    <Modal show={attachmentModalIsOpen} onHide={attachmentModalToggle}>
      <Modal.Header show={attachmentModalIsOpen} onHide={attachmentModalToggle}>
        PV No.: {pvImageInfo.pv_no}
      </Modal.Header>
      <Modal.Body className="text-center">
        <Container>
          <div className="image-container">
            {pvImageInfo.length ? (
              pvImageInfo.map((item, index) =>
                imageExtensions.some((extension) =>
                  item.image_url.toLowerCase().endsWith(extension)
                ) ? (
                  <Card.Img
                    top
                    src={`${apiURL}/uploads/${item.image_url}`}
                    alt={"pv"}
                  />
                ) : (
                  <Card.Body>
                    <Card.Link
                      href={`${apiURL}/uploads/${item.image_url}`}
                      target="_blank"
                    >
                      View PDF
                    </Card.Link>
                  </Card.Body>
                )
              )
            ) : (
              <h4>No attachment found.</h4>
            )}
          </div>
        </Container>
      </Modal.Body>
    </Modal>
  );
};
